export const aboutIntro =
  "Agentic Plus builds the operating layer between marketing evidence, creative production, and accountable execution.";

export const aboutFacts = [
  {
    title: "Our focus",
    description:
      "We build tools that make creative growth repeatable: evidence is reusable, production is traceable, and decisions remain reviewable.",
  },
  {
    title: "Our operating principle",
    description:
      "Verified facts come before model inference, and high-cost or external actions require explicit human approval.",
  },
  {
    title: "Who we serve",
    description:
      "Agentic Plus is designed for brand teams, agencies, and media buyers responsible for producing and improving campaign creative.",
  },
  {
    title: "How we work",
    description:
      "We favor narrow, auditable workflows over opaque automation. Every mission preserves its inputs, outputs, approvals, and next step.",
  },
];

export const contactMethods = [
  {
    title: "Request a demo",
    description:
      "Tell us about your brand, clients, creative volume, and current production workflow. We will show the relevant parts of Agentic Plus.",
    action: {
      label: `Email ${"hello@agenticplus.dev"}`,
      href: "mailto:hello@agenticplus.dev?subject=Agentic%20Plus%20demo%20request",
    },
  },
  {
    title: "Agency onboarding",
    description:
      "Ask about workspace setup, brand knowledge transfer, review permissions, and phased rollout for multiple clients.",
    action: {
      label: "Email the agency team",
      href: "mailto:hello@agenticplus.dev?subject=Agency%20onboarding",
    },
  },
  {
    title: "Security review",
    description:
      "Request our security overview or share your review questionnaire before a procurement decision.",
    action: {
      label: "Email security",
      href: "mailto:hello@agenticplus.dev?subject=Security%20review",
    },
  },
];

export const securityControls = [
  {
    title: "Workspace isolation",
    description:
      "Brand, product, asset, and task data are scoped to an authorized workspace. Cross-tenant access is not part of the user model.",
    points: ["Tenant-scoped records", "Workspace-bound context", "Least-privilege access"],
  },
  {
    title: "Permission boundaries",
    description:
      "Reading, generating, uploading, and publishing are separate permissions. A chat instruction does not elevate a user's underlying access.",
    points: [
      "Role-based access",
      "Destination-specific authorization",
      "Explicit generation limits",
    ],
  },
  {
    title: "Approval controls",
    description:
      "High-cost generation and external actions require review of the relevant version or execution plan.",
    points: ["Cost confirmation", "Human review", "Approval tied to version or plan"],
  },
  {
    title: "Traceability",
    description:
      "Evidence, prompts, model context, versions, task state, approvals, and delivery results remain connected for audit.",
    points: ["Source evidence", "Version lineage", "Approval and execution record"],
  },
  {
    title: "Unknown-state handling",
    description:
      "A timeout or ambiguous provider response is treated as unknown until verified, not silently retried or marked successful.",
    points: ["Verification step", "Provider request ID where available", "Clear next action"],
  },
  {
    title: "Compensation paths",
    description:
      "Reversible external actions have a defined compensation path. Irreversible portions are disclosed before execution.",
    points: ["Undo where supported", "Stop or pause action", "Exception record"],
  },
];

export const privacySections = [
  {
    title: "Information we process",
    description:
      "We process account contact information, workspace content, product and brand materials, creative inputs and outputs, approval records, and operational logs needed to provide the service.",
  },
  {
    title: "How we use information",
    description:
      "We use this information to operate workspaces, provide production and review workflows, maintain security, prevent abuse, provide support, and improve the service under the terms agreed with the customer.",
  },
  {
    title: "Customer control",
    description:
      "Customer administrators control workspace membership and authorized integrations. Customer content belongs to the customer under the applicable service agreement.",
  },
  {
    title: "Service providers",
    description:
      "We use service providers for hosting, storage, model inference, email, analytics, and support. Providers receive only the information needed to perform their function.",
  },
  {
    title: "Retention",
    description:
      "We retain workspace content and operational records while an account is active or as required by agreement, law, or legitimate security needs. Deletion and export processes are available to customer administrators.",
  },
  {
    title: "Your choices",
    description:
      "You can contact us to exercise rights available under applicable law, ask about data handling, or request help with export and deletion.",
  },
];

export const termsSections = [
  {
    title: "Service description",
    description:
      "Agentic Plus provides software for creative research, planning, production, review, versioning, and governed delivery workflows. Features depend on the purchased plan and enabled permissions.",
  },
  {
    title: "Accounts and workspaces",
    description:
      "Customer administrators are responsible for accounts, roles, workspace configuration, approvals, and the accuracy of materials submitted to the service.",
  },
  {
    title: "Acceptable use",
    description:
      "Do not use Agentic Plus to infringe rights, process materials without permission, bypass platform policies, create misleading claims, or attempt to access another workspace.",
  },
  {
    title: "Customer materials",
    description:
      "Customer materials remain customer property. The customer grants the limited rights needed to store, process, display, and produce outputs within the service.",
  },
  {
    title: "Generated outputs",
    description:
      "Customers are responsible for reviewing outputs, confirming brand and legal suitability, and obtaining any required approval before external use.",
  },
  {
    title: "Third-party platforms",
    description:
      "Connections to advertising, storage, or model providers follow those providers' terms. Agentic Plus does not guarantee availability of a third-party platform outside its control.",
  },
  {
    title: "Liability",
    description:
      "These terms allocate responsibility for the service. The detailed limitations, warranties, and remedies are set out in the signed commercial agreement or the posted terms governing your account.",
  },
];
